import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Typography, Alert, Space, Button, Steps, Table, message } from 'antd';
import {
  DownloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../api';
import type { MyOrganiserCompetition } from '../types';
import {
  getCompetitionOverviewColumns,
  getAgentConnectionsColumns,
} from './liveCompetitionSetupColumns';

const { Title, Text, Paragraph } = Typography;

/**
 * Instructions and read-only status for FinishLynx live streaming.
 * Competition creation and per-PC folders are done in the desktop agent; this page
 * lists meets and whether an agent has heartbeated recently (one status row per meet).
 */
const LiveCompetitionSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSetup = user?.competitionSetup === true || user?.admin === true;

  interface AgentVersion {
    version: string;
    setup_download_url: string;
  }
  const [agentVersion, setAgentVersion] = useState<AgentVersion | null>(null);

  const [myCompetitions, setMyCompetitions] = useState<MyOrganiserCompetition[]>([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);

  useEffect(() => {
    axios
      .get<AgentVersion>(`${API_BASE_URL}/api/agent/version`)
      .then((res) => setAgentVersion(res.data))
      .catch(() => {});
  }, []);

  const fetchMyCompetitions = useCallback(async () => {
    setLoadingCompetitions(true);
    try {
      const headers = await getAuthHeaders();
      if (!headers?.Authorization) return;
      const res = await axios.get<{ competitions: MyOrganiserCompetition[] }>(
        `${API_BASE_URL}/api/live/my-competitions`,
        { headers },
      );
      setMyCompetitions(res.data.competitions || []);
    } catch {
      setMyCompetitions([]);
    } finally {
      setLoadingCompetitions(false);
    }
  }, []);

  useEffect(() => {
    if (canSetup) fetchMyCompetitions();
  }, [canSetup, fetchMyCompetitions]);

  const handleArchive = useCallback(
    async (compId: number) => {
      try {
        const headers = await getAuthHeaders();
        if (!headers?.Authorization) {
          message.error('Please sign in again');
          return;
        }
        await axios.post(`${API_BASE_URL}/api/live/competition/${compId}/archive`, {}, { headers });
        message.success('Competition archived');
        fetchMyCompetitions();
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err?.response?.data?.error || 'Archiving failed.');
      }
    },
    [fetchMyCompetitions],
  );

  const competitionsTableRows = useMemo(() => {
    return [...myCompetitions].sort((a, b) => Number(b.is_active) - Number(a.is_active));
  }, [myCompetitions]);

  const competitionsColumns = useMemo(
    () => getCompetitionOverviewColumns(handleArchive),
    [handleArchive],
  );
  const connectionsColumns = useMemo(() => getAgentConnectionsColumns(), []);

  if (user && !canSetup) {
    return (
      <div className="page-container">
        <Title level={3}>Live competition setup</Title>
        <Alert
          message="Not available"
          description="This feature is available on paid plans. Contact your administrator to enable live organiser access for your account."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Title level={3}>Live competition setup</Title>
      <Paragraph type="secondary">
        Create meets and attach each FinishLynx PC using the <strong>desktop Live Agent</strong> (sign in with
        this account). This page shows your meets and whether an agent has checked in recently. Each meet has one
        heartbeat on the server — if you run several PCs on the same meet, whichever agent checked in last updates
        the status.
      </Paragraph>

      <Card title="How to set up" style={{ marginTop: 16 }}>
        <Steps
          direction="vertical"
          size="small"
          items={[
            {
              title: 'Install the agent',
              description: 'Download the Windows installer below and run it on each FinishLynx machine.',
            },
            {
              title: 'Sign in',
              description: 'Open the agent → use the same email and password as this web account (Firebase).',
            },
            {
              title: 'Configure the meet',
              description:
                'In the agent → Competitions: refresh the list, create a meet or pick an active one, then Configure this PC and choose input/output folders with Browse.',
            },
            {
              title: 'Start streaming',
              description: 'Home → Start. FinishLynx should point its scoreboard script at this PC (see deployment docs).',
            },
            {
              title: 'Check status here',
              description: 'Agents send a heartbeat about every 60 seconds. Use Refresh status below to update the table.',
            },
          ]}
        />
      </Card>

      <Card
        title={
          <span>
            <DownloadOutlined /> Download the FinishLynx Live Agent
          </span>
        }
        style={{ marginTop: 16 }}
      >
        <Space direction="vertical">
          <Text type="secondary">
            Install on the Windows machine running FinishLynx. The agent watches the input & output folders and uploads to
            the live API.
          </Text>
          <Space wrap>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={agentVersion?.setup_download_url ?? '#'}
              disabled={!agentVersion}
              target="_blank"
              rel="noopener noreferrer"
            >
              {agentVersion ? `Download installer v${agentVersion.version}` : 'Loading…'}
            </Button>
          </Space>
        </Space>
      </Card>

      <Card title="Your competitions" style={{ marginTop: 16 }}>
        {myCompetitions.length === 0 && !loadingCompetitions ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">No competitions yet. Create one in the desktop agent (Competitions tab).</Text>
            <Button onClick={() => fetchMyCompetitions()}>Refresh status</Button>
          </Space>
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Active meets are listed first. <strong>Meet</strong> is archive state; <strong>Agent</strong> is live
              connection (heartbeat within two minutes). Scroll horizontally on small screens to see every column.
            </Paragraph>
            <div
              data-testid="live-setup-competitions-table-wrap"
              style={{ width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
              <Table<MyOrganiserCompetition>
                size="small"
                rowKey="id"
                columns={competitionsColumns}
                dataSource={competitionsTableRows}
                pagination={false}
                loading={loadingCompetitions}
                scroll={{ x: 'max-content' }}
              />
            </div>
            <Button style={{ marginTop: 12 }} onClick={() => fetchMyCompetitions()}>
              Refresh status
            </Button>
          </>
        )}
      </Card>

      <Card title="Agent connections (per meet)" style={{ marginTop: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          <strong>Live</strong> means an agent sent a heartbeat within the last two minutes. Folder paths are the values
          last saved for that meet (often from the machine that ran setup). Scroll horizontally on smaller screens.
        </Paragraph>
        <div
          data-testid="live-setup-connections-table-wrap"
          style={{ width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          <Table<MyOrganiserCompetition>
            size="small"
            rowKey="id"
            columns={connectionsColumns}
            dataSource={competitionsTableRows}
            pagination={false}
            loading={loadingCompetitions}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      <Alert
        style={{ marginTop: 24 }}
        message="View live results"
        description={
          <>
            When the agent is running, open{' '}
            <Button type="link" onClick={() => navigate('/live')} style={{ padding: 0 }}>
              Live results
            </Button>{' '}
            — no login required for viewers.
          </>
        }
        type="info"
        showIcon
        icon={<ThunderboltOutlined />}
      />
    </div>
  );
};

export default LiveCompetitionSetup;
